import React, {useCallback, useEffect, useState} from 'react';
import {
  Box,
  Button,
  chakra,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
} from '@chakra-ui/react';
import {useSaveTagMutation, useTags} from '../hooks/api/tags';
import {TagResponse} from '../shared/tags';
import _ from 'lodash';
import {useForm} from 'react-hook-form';
import {AddFilled} from '@carbon/icons-react';
import {TagName} from '../shared/validation';
import {pluralize} from '../lib/helpers';
import {ActionContext, useAccountActions} from '../hooks/api/actions';
import {ObjectId} from 'bson';

interface AccountTagsEditorModalProps {
  isOpen: boolean;
  onClose: () => void;

  actionContext?: ActionContext;
  bulkNumberSelected?: number;
  onTagsUpdated?: (tags: ObjectId[]) => void;
  selectedTags: ObjectId[];
  tagOnly: boolean;
  mode?: 'edit' | 'remove';
  filteredTagIds?: ObjectId[];
}

export function AccountTagsEditorModal({
  isOpen,
  onClose,
  mode = 'edit',
  ...rest
}: AccountTagsEditorModalProps) {
  const headerText =
    mode === 'remove'
      ? 'Remove from Lists'
      : rest.tagOnly
        ? 'Add to Lists'
        : 'Save & Add to List';
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb="0">{headerText}</ModalHeader>
        <ModalCloseButton />
        <ModalBody p="0">
          <AccountTagsEditorModalBody onClose={onClose} mode={mode} {...rest} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

const AccountTagsEditorModalBody = ({
  actionContext,
  bulkNumberSelected,
  onClose,
  onTagsUpdated,
  selectedTags,
  tagOnly,
  mode = 'edit',
  filteredTagIds = [],
}: Omit<AccountTagsEditorModalProps, 'isOpen'>) => {
  const tags = useTags({type: 'static'});
  const [localSelectedTags, setLocalSelectedTags] = useState<ObjectId[]>([]);

  useEffect(() => {
    setLocalSelectedTags(selectedTags);
  }, [selectedTags]);

  const onCheckboxClicked = useCallback(
    (tag: TagResponse, checked: boolean) => {
      setLocalSelectedTags((prevState) => {
        if (checked) {
          return [...prevState, tag._id];
        } else {
          const idx = prevState.findIndex((item) => item.equals(tag._id));
          if (idx === undefined) {
            return prevState;
          }

          const updatedTags = [...prevState];
          updatedTags.splice(idx, 1);
          return updatedTags;
        }
      });
    },
    []
  );

  const accountActions = useAccountActions(actionContext);
  const updateAccount = (mode: 'edit' | 'save' | 'remove') => {
    if (!actionContext) {
      return;
    }

    const tagsToAdd =
      mode === 'edit'
        ? _.differenceWith(localSelectedTags, selectedTags, _.isEqual)
        : [];
    const tagsToRemove =
      mode === 'edit'
        ? _.differenceWith(selectedTags, localSelectedTags, _.isEqual)
        : mode === 'remove'
          ? localSelectedTags
          : [];

    accountActions.mutate(
      {
        action: {
          name: 'tag',
          tagsToAdd,
          tagsToRemove,
        },
      },
      {
        onSuccess: () => {
          onTagsUpdated?.(localSelectedTags);
          onClose();
        },
      }
    );
  };

  const isSaveButtonDisabled =
    _.xor(
      localSelectedTags.map((id) => id.toString()),
      selectedTags.map((id) => id.toString())
    ).length === 0;

  let subHeaderText = '';
  if (!bulkNumberSelected) {
    subHeaderText = 'Select lists for this account';
  } else {
    subHeaderText = pluralize(
      bulkNumberSelected,
      'selected account',
      'selected accounts'
    );
  }

  let saveButtonText = '';
  if (!tagOnly) {
    saveButtonText = 'Save + Add to Lists';
  } else if (mode === 'remove') {
    saveButtonText = 'Remove accounts from selected lists';
  } else {
    saveButtonText = `Add ${bulkNumberSelected ? 'accounts' : 'account'} to selected lists`;
  }

  const createTagMutation = useSaveTagMutation();

  const {
    formState: {errors},
    handleSubmit,
    getValues,
    register,
    reset: resetForm,
  } = useForm<{tagName: string}>({
    defaultValues: {
      tagName: '',
    },
  });

  const handleNewTagSubmit = () => {
    if (createTagMutation.isPending) {
      return;
    }

    createTagMutation.mutate(
      {name: getValues('tagName'), type: 'static'},
      {
        onSuccess: (newTag) => {
          // Reset the form to the default state.
          resetForm();
          // Auto-select newly-added tag
          setLocalSelectedTags((prevState) => [...prevState, newTag._id]);
        },
      }
    );
  };

  // If we're in remove mode, only show tags that are on the selected accounts
  const filteredTags =
    mode === 'remove'
      ? tags.data?.filter((tag) =>
          filteredTagIds.length
            ? filteredTagIds.some((id) => id.equals(tag._id))
            : true
        )
      : tags.data;

  return (
    <>
      <Box fontWeight={500} fontSize={14} mb={6} color="kgray.300" mx="6">
        {subHeaderText}
      </Box>
      {mode === 'edit' && (
        <Box
          as="form"
          onSubmit={handleSubmit(handleNewTagSubmit)}
          my={4}
          mx="6"
        >
          <FormControl
            isDisabled={createTagMutation.isPending || accountActions.isPending}
          >
            <InputGroup as={Flex} direction="column" gap="2">
              <Input
                autoComplete="off"
                maxLength={30}
                placeholder="Create a new list"
                {...register('tagName', {
                  minLength: TagName.minLength,
                  maxLength: TagName.maxLength,
                  pattern: TagName.pattern,
                  required: true,
                  setValueAs: (value) => value.trim(),
                })}
              />
              <InputRightElement p={2}>
                {createTagMutation.isPending ? (
                  <Spinner color="kblue.200" />
                ) : (
                  <Button type="submit" p={0} size="sm">
                    <ChakraAddFilled
                      color={'kblue.300'}
                      height={'80%'}
                      width={'80%'}
                    />
                  </Button>
                )}
              </InputRightElement>
              {errors.tagName && (
                <Box color="red.500" fontSize="sm" ml={2}>
                  List names should be between {TagName.minLength} and{' '}
                  {TagName.maxLength} characters, and contain only letters and
                  numbers.
                </Box>
              )}
            </InputGroup>
          </FormControl>
        </Box>
      )}

      <Flex direction="column" maxH="30vh" minH="250px" overflowY="auto" px="6">
        {tags.isPending ? (
          <Spinner m="auto" />
        ) : (
          filteredTags?.map((tag) => {
            return (
              <Checkbox
                key={tag._id.toString()}
                my={2}
                mx={1}
                onChange={(e) => onCheckboxClicked(tag, e.target.checked)}
                isChecked={localSelectedTags.some((id) => id.equals(tag._id))}
              >
                {tag.name}
              </Checkbox>
            );
          })
        )}
      </Flex>

      <Divider />

      <Flex p="6" gap="6" justifyContent="space-between">
        <Button
          colorScheme="kbuttonblue"
          isDisabled={isSaveButtonDisabled}
          isLoading={accountActions.isPending}
          onClick={() => updateAccount(mode)}
          flex="1"
        >
          {saveButtonText}
        </Button>
        {!tagOnly && (
          <Button
            color="kgray.300"
            isLoading={accountActions.isPending}
            onClick={() => updateAccount('save')}
            variant="ghost"
            flex="1"
          >
            Save Only
          </Button>
        )}
      </Flex>
    </>
  );
};

const ChakraAddFilled = chakra(AddFilled);
